import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";

export type ImportProgressState = {
  phase: "running" | "done";
  total: number;
  imported: number;
  failed: number;
  cancelled: boolean;
};

interface WordMasterImportProgressDialogProps {
  state: ImportProgressState | null;
  onCancel: () => void;
  onClose: () => void;
  onDownloadFailures: () => void;
}

function StatCell({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "success" | "danger" | "muted";
}) {
  const toneClass =
    tone === "success"
      ? "text-green-600"
      : tone === "danger"
        ? "text-destructive"
        : tone === "muted"
          ? "text-muted-foreground"
          : "text-foreground";
  return (
    <div className="rounded-lg border border-border/60 bg-secondary/30 px-3 py-2.5 text-center">
      <p className={`text-xl font-bold tabular-nums ${toneClass}`}>
        {value.toLocaleString()}
      </p>
      <p className="text-muted-foreground mt-0.5 text-xs">{label}</p>
    </div>
  );
}

export function WordMasterImportProgressDialog({
  state,
  onCancel,
  onClose,
  onDownloadFailures,
}: WordMasterImportProgressDialogProps) {
  if (!state) return null;

  const { phase, total, imported, failed, cancelled } = state;
  const processed = imported + failed;
  const pending = Math.max(0, total - processed);
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
  const running = phase === "running";

  return (
    <Dialog
      open
      // Block dismiss while running so progress isn't lost; allow it when done.
      onOpenChange={open => {
        if (!open && !running) onClose();
      }}
    >
      <DialogContent
        className="max-w-md"
        onInteractOutside={e => running && e.preventDefault()}
        onEscapeKeyDown={e => running && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {running ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Importing words…
              </>
            ) : cancelled ? (
              <>
                <XCircle className="text-muted-foreground h-5 w-5" />
                Import cancelled
              </>
            ) : failed === 0 ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Import complete
              </>
            ) : (
              <>
                <CheckCircle2 className="h-5 w-5 text-amber-500" />
                Import finished with errors
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {running
              ? "Please keep this window open until the import finishes."
              : cancelled
                ? "The import was stopped. Rows already processed are saved."
                : "Here's a summary of the import."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <div className="text-muted-foreground mb-1.5 flex items-center justify-between text-sm">
              <span>{percent}% complete</span>
              <span className="tabular-nums">
                {processed.toLocaleString()} / {total.toLocaleString()}
              </span>
            </div>
            <Progress value={percent} />
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatCell label="Total" value={total} tone="muted" />
            <StatCell label="Imported" value={imported} tone="success" />
            <StatCell label="Failed" value={failed} tone="danger" />
            <StatCell label="Pending" value={pending} tone="muted" />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {running ? (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          ) : (
            <>
              {failed > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onDownloadFailures}
                  className="gap-1.5"
                >
                  <Download className="h-4 w-4" />
                  Download failed rows ({failed.toLocaleString()})
                </Button>
              )}
              <Button
                type="button"
                onClick={onClose}
                className="bg-primary hover:bg-primary/90"
              >
                Close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
